/**
 * Tiny hydration layer for the SSR menu + checkout pages.
 *
 * Menu page:
 *  - +/- stepper buttons control the qty input
 *  - Quantity changes recompute the live summary client-side and debounce-save
 *    via POST /md/v1/menu/{id}/draft/line (~400ms; keepalive on unload)
 *  - "Continue to checkout" flushes the pending save before navigating
 *
 * Checkout page:
 *  - Form is a plain <form method="post"> handled server-side; this script
 *    only wires the tip chip UI and a live tip/total preview.
 *
 * Logging is intentionally chatty (prefix `[md]`) to make debugging trivial.
 */

(function () {
    var prep = {};

    function log() {
        if (typeof console === 'undefined' || !console.log) {
            return;
        }
        var a = ['[md]'].concat([].slice.call(arguments));
        console.log.apply(console, a);
    }

    function fmtMoney(n) {
        return '$' + Number(n || 0).toFixed(2);
    }

    /* ============================================================
     * CUTOFF COUNTDOWN (menu header + menus list hero)
     * ========================================================== */

    function cutoffRemainPartsFromMs(ms) {
        var sec = Math.floor(ms / 1000);
        var d = Math.floor(sec / 86400);
        sec -= d * 86400;
        var h = Math.floor(sec / 3600);
        sec -= h * 3600;
        var m = Math.floor(sec / 60);
        sec -= m * 60;
        var s = sec;
        return { d: d, h: h, m: m, s: s };
    }

    function cutoffFormatRemainCompact(parts, cc) {
        var sep = cc.sep || ' · ';
        var sd = cc.suffixDay || 'd';
        var sh = cc.suffixHour || 'h';
        var sm = cc.suffixMinute || 'm';
        var ss = cc.suffixSecond || 's';
        var bits = [];
        if (parts.d > 0) {
            bits.push(String(parts.d) + sd);
        }
        if (parts.h > 0) {
            bits.push(String(parts.h) + sh);
        }
        if (parts.m > 0) {
            bits.push(String(parts.m) + sm);
        }
        bits.push(String(parts.s) + ss);
        return bits.join(sep);
    }

    function mountCutoffCountdown() {
        var prepGlobal = typeof mdMealPrep !== 'undefined' ? mdMealPrep : {};
        var cc = prepGlobal.cutoffCountdown;
        if (!cc || typeof cc.suffixSecond !== 'string') {
            cc = {
                sep: ' · ',
                suffixDay: 'd',
                suffixHour: 'h',
                suffixMinute: 'm',
                suffixSecond: 's',
                closed: 'Closed',
            };
        }
        var roots = document.querySelectorAll('[data-md-cutoff-unix]');
        for (var i = 0; i < roots.length; i++) {
            (function (root) {
                var raw = root.getAttribute('data-md-cutoff-unix');
                var endUnix = parseInt(raw, 10);
                if (isNaN(endUnix) || endUnix <= 0) {
                    return;
                }
                var live = root.querySelector('.md-cutoff-countdown__live');
                if (!live) {
                    return;
                }
                var unitEls = live.querySelectorAll('[data-md-cutoff-unit]');
                function tick() {
                    var leftMs = endUnix * 1000 - Date.now();
                    if (leftMs <= 0) {
                        live.textContent = cc.closed || '';
                        return false;
                    }
                    var parts = cutoffRemainPartsFromMs(leftMs);
                    if (unitEls.length) {
                        for (var u = 0; u < unitEls.length; u++) {
                            var el = unitEls[u];
                            var k = el.getAttribute('data-md-cutoff-unit');
                            var val = parts[k];
                            if (val === undefined) {
                                continue;
                            }
                            var nEl = el.querySelector('.md-cutoff-unit__n');
                            if (nEl) {
                                nEl.textContent = String(val);
                            }
                            if (k === 's') {
                                continue;
                            }
                            if (val === 0) {
                                el.classList.add('is-zero');
                            } else {
                                el.classList.remove('is-zero');
                            }
                        }
                    } else {
                        live.textContent = cutoffFormatRemainCompact(parts, cc);
                    }
                    return true;
                }
                tick();
                var id = setInterval(function () {
                    if (!tick()) {
                        clearInterval(id);
                    }
                }, 1000);
            })(roots[i]);
        }
    }

    /* ============================================================
     * MENU STEP
     * ========================================================== */

    function recomputeFromInputs(shell) {
        var min = parseInt(shell.getAttribute('data-min-meals'), 10);
        if (isNaN(min) || min < 0) {
            min = 5;
        }
        var exempt = shell.getAttribute('data-exempt-minimum') === '1';
        var lineQty = 0;
        var subtotal = 0;
        var inputs = shell.querySelectorAll('.md-meal-qty');
        for (var i = 0; i < inputs.length; i++) {
            var inp = inputs[i];
            if (inp.disabled) {
                continue;
            }
            var q = parseInt(inp.value, 10) || 0;
            var p = parseFloat(String(inp.getAttribute('data-unit-price') || '0').replace(/,/g, '')) || 0;
            lineQty += q;
            subtotal += q * p;
        }
        return {
            lineQty: lineQty,
            subtotal: Math.round(subtotal * 100) / 100,
            min: min,
            exempt: exempt
        };
    }

    function setContinueEnabled(can) {
        var nodes = [];
        var primary = document.getElementById('md-step-continue');
        if (primary && primary.classList.contains('md-step-continue')) {
            nodes.push(primary);
        }
        var sticky = typeof document.querySelectorAll === 'function'
            ? document.querySelectorAll('a.md-step-continue--sticky')
            : [];
        var si;
        for (si = 0; si < sticky.length; si++) {
            nodes.push(sticky[si]);
        }
        var i;
        for (i = 0; i < nodes.length; i++) {
            var cont = nodes[i];
            if (can) {
                cont.classList.remove('is-disabled');
                cont.removeAttribute('aria-disabled');
                cont.removeAttribute('tabindex');
            } else {
                cont.classList.add('is-disabled');
                cont.setAttribute('aria-disabled', 'true');
                cont.setAttribute('tabindex', '-1');
            }
        }
    }

    function applyLiveSummary(shell, mealSumRef, saveBusy) {
        if (arguments.length < 3) {
            saveBusy = false;
        }
        var r = recomputeFromInputs(shell);
        var lineQty = r.lineQty;
        var subtotal = r.subtotal;
        if (mealSumRef && shell.hasAttribute('data-order-summary-line-qty')) {
            var bq = parseInt(shell.getAttribute('data-order-summary-line-qty'), 10) || 0;
            var bs = parseFloat(String(shell.getAttribute('data-order-summary-subtotal') || '0').replace(/,/g, '')) || 0;
            if (mealSumRef.q0 === null) {
                mealSumRef.q0 = r.lineQty;
                mealSumRef.s0 = r.subtotal;
            }
            lineQty = bq - mealSumRef.q0 + r.lineQty;
            subtotal = Math.round((bs - mealSumRef.s0 + r.subtotal) * 100) / 100;
        }
        var countEl = document.getElementById('md-step-count');
        if (countEl) {
            countEl.textContent = String(lineQty);
        }
        var countSticky =
            typeof document.querySelectorAll === 'function'
                ? document.querySelectorAll('.md-step-count--sticky')
                : [];
        var ci;
        for (ci = 0; ci < countSticky.length; ci++) {
            countSticky[ci].textContent = String(lineQty);
        }
        var subEl = document.getElementById('md-step-subtotal');
        if (subEl) {
            subEl.textContent = fmtMoney(subtotal);
        }
        var fm = fmtMoney(subtotal);
        var subSticky =
            typeof document.querySelectorAll === 'function'
                ? document.querySelectorAll('.md-step-subtotal--sticky')
                : [];
        var sj;
        for (sj = 0; sj < subSticky.length; sj++) {
            subSticky[sj].textContent = fm;
        }
        var canCont = r.exempt || lineQty >= r.min;
        setContinueEnabled(canCont && !saveBusy);
        var contNodes = [];
        var contPrimary = document.getElementById('md-step-continue');
        if (contPrimary && contPrimary.classList.contains('md-step-continue')) {
            contNodes.push(contPrimary);
        }
        var contStickyAnchors =
            typeof document.querySelectorAll === 'function'
                ? document.querySelectorAll('a.md-step-continue--sticky')
                : [];
        var ck;
        for (ck = 0; ck < contStickyAnchors.length; ck++) {
            contNodes.push(contStickyAnchors[ck]);
        }
        var ct;
        for (ct = 0; ct < contNodes.length; ct++) {
            var contEl = contNodes[ct];
            if (saveBusy) {
                contEl.setAttribute('aria-busy', 'true');
                contEl.setAttribute('title', 'Saving…');
            } else {
                contEl.removeAttribute('aria-busy');
                contEl.removeAttribute('title');
            }
        }

        if (!r.exempt) {
            var panels = typeof document.querySelectorAll === 'function' ? document.querySelectorAll('[data-order-min-hint-panel]') : [];
            var i;
            var lineOk = lineQty >= r.min;
            for (i = 0; i < panels.length; i++) {
                var hintWrap = panels[i];
                if (lineOk) {
                    hintWrap.hidden = true;
                } else {
                    hintWrap.hidden = false;
                    var needRm = Math.max(0, r.min - lineQty);
                    var needSpan = hintWrap.querySelector('[data-order-min-text]');
                    var rtpl = hintWrap.getAttribute('data-remain-tpl');
                    if (needSpan && rtpl) {
                        needSpan.textContent = rtpl.replace('%s', String(needRm));
                    }
                }
            }
        }
        var detailAll = shell.querySelector('[data-md-meal-detail]');
        if (detailAll) {
            var sumEl = detailAll.querySelector('.js-md-meal-all-options-sum-n');
            if (sumEl) {
                var totalPlates = 0;
                detailAll.querySelectorAll('.md-meal-qty').forEach(function (inp) {
                    if (!inp.disabled) {
                        totalPlates += parseInt(inp.value, 10) || 0;
                    }
                });
                sumEl.textContent = String(totalPlates);
            }
        }
        return r;
    }

    function wireMealDetailProduct(shell, applySummary) {
        var detail = shell.querySelector('[data-md-meal-detail]');
        if (!detail) {
            return;
        }
        function selectedPlateRadio() {
            return detail.querySelector('.md-meal-detail__plate-radio:checked');
        }
        function inCartQtyInput() {
            var rad = selectedPlateRadio();
            if (rad) {
                var li = rad.closest('.md-meal-detail__plate');
                return li ? li.querySelector('.md-meal-qty') : null;
            }
            var simple = detail.querySelector('.md-meal-detail__plates--simple');
            if (simple) {
                return simple.querySelector('.md-meal-qty');
            }
            return detail.querySelector('.md-meal-qty');
        }
        function updateInCartLabel() {
            var inp = inCartQtyInput();
            var n = inp ? (parseInt(inp.value, 10) || 0) : 0;
            detail.querySelectorAll('.md-meal-detail__in-cart-qty').forEach(function (el) {
                el.textContent = String(n);
            });
        }
        function syncPlateRows() {
            detail.querySelectorAll('.md-meal-detail__plate[data-plate-row]').forEach(function (li) {
                li.classList.remove('is-plate-selected');
            });
            var rad = selectedPlateRadio();
            if (rad) {
                var li = rad.closest('.md-meal-detail__plate');
                if (li) {
                    li.classList.add('is-plate-selected');
                }
            }
            updateInCartLabel();
            applySummary();
        }
        detail.addEventListener('change', function (e) {
            var t = e.target;
            if (t && t.classList && t.classList.contains('md-meal-detail__plate-radio')) {
                syncPlateRows();
            }
        });
        detail.addEventListener('input', function (e) {
            var t = e.target;
            if (t && t.classList && t.classList.contains('md-meal-qty')) {
                updateInCartLabel();
            }
        });
        var addBtn = detail.querySelector('.md-meal-detail__add-btn');
        if (addBtn) {
            addBtn.addEventListener('click', function () {
                var addInp = detail.querySelector('.md-meal-detail-add-qty');
                var add = addInp ? (parseInt(addInp.value, 10) || 0) : 1;
                if (add < 1) {
                    add = 1;
                }
                if (add > 30) {
                    add = 30;
                }
                var qInp = inCartQtyInput();
                if (!qInp || qInp.disabled) {
                    return;
                }
                var v = (parseInt(qInp.value, 10) || 0) + add;
                if (v > 30) {
                    v = 30;
                }
                qInp.value = String(v);
                qInp.dispatchEvent(new Event('input', { bubbles: true }));
                updateInCartLabel();
            });
        }
        syncPlateRows();
    }

    function setSaveStatus(state) {
        var el = document.getElementById('md-menu-step-save-status');
        if (!el) { return; }
        if (state === 'saving') {
            el.textContent = 'Saving…';
            el.className = 'md-step-summary__save-status is-saving';
        } else if (state === 'saved') {
            el.textContent = 'Saved';
            el.className = 'md-step-summary__save-status is-saved';
            setTimeout(function () {
                if (el.classList.contains('is-saved')) {
                    el.textContent = el.getAttribute('data-idle-label') || 'Pending';
                    el.className = 'md-step-summary__save-status';
                }
            }, 1500);
        } else if (state === 'error') {
            el.textContent = 'Save failed — retrying';
            el.className = 'md-step-summary__save-status is-error';
        } else {
            el.textContent = el.getAttribute('data-idle-label') || 'Pending';
            el.className = 'md-step-summary__save-status';
        }
    }

    function mountMenuStep() {
        var M = window.MDMeals;
        if (!M || !M.api) {
            log('menu: MDMeals.api missing; skipping hydration');
            return;
        }
        var shell = document.querySelector('.md-prep-shell[data-md-page="menu"], .md-prep-shell[data-md-page="meal"]');
        if (!shell) { return; }
        if (shell.getAttribute('data-orderable') !== '1') {
            log('menu: orderable=0; skipping hydration');
            return;
        }
        if (shell.getAttribute('data-readonly') === '1') {
            log('menu: readonly=1; skipping hydration');
            return;
        }
        if (shell.querySelectorAll('.md-meal-qty').length === 0) {
            log('menu: no qty inputs; skipping hydration');
            return;
        }

        var menuId = parseInt(shell.getAttribute('data-menu-id'), 10) || prep.menuId || 0;
        if (!menuId) {
            log('menu: no menu id; skipping hydration');
            return;
        }

        var mealSummaryRef = shell.hasAttribute('data-order-summary-line-qty')
            ? { q0: null, s0: null }
            : null;
        var pendingTimer = null;
        var pendingByLine = {};
        var flushInFlight = false;
        function isDraftSaveBusy() {
            return !!pendingTimer || Object.keys(pendingByLine).length > 0 || flushInFlight;
        }
        function doSummary() {
            applyLiveSummary(shell, mealSummaryRef, isDraftSaveBusy());
        }

        function lineKey(mealId, plateId) {
            return String(mealId) + ':' + String(plateId);
        }

        function flushPending(keepalive) {
            if (pendingTimer) {
                clearTimeout(pendingTimer);
                pendingTimer = null;
            }
            var keys = Object.keys(pendingByLine);
            if (keys.length === 0) {
                return Promise.resolve();
            }
            flushInFlight = true;
            doSummary();
            var sends = keys.map(function (key) {
                var parts = key.split(':');
                var mid = parseInt(parts[0], 10);
                var pid = parseInt(parts[1], 10) || 0;
                var qty = pendingByLine[key];
                delete pendingByLine[key];
                log('menu:save ->', { meal_id: mid, plate_id: pid, qty: qty, keepalive: !!keepalive });
                return M.api('menu/' + menuId + '/draft/line', {
                    method: 'POST',
                    body: { meal_id: mid, plate_id: pid, qty: qty },
                    keepalive: !!keepalive
                }).then(function (res) {
                    log('menu:save ok', res);
                    if (res && res.debug) {
                        var dbg = res.debug;
                        if (!dbg.write_ok) {
                            console.warn('[md] update_field returned false (ACF write may have failed)', dbg);
                        }
                        var vk = String(mid) + ':' + String(pid);
                        if (dbg.verify_qty_map && typeof dbg.verify_qty_map[vk] === 'undefined' && qty > 0) {
                            console.warn('[md] post-save verify: line ' + vk + ' qty=' + qty + ' is NOT in stored repeater', dbg);
                        } else if (dbg.verify_qty_map && qty > 0 && Number(dbg.verify_qty_map[vk]) !== qty) {
                            console.warn('[md] post-save verify: stored qty ' + dbg.verify_qty_map[vk] + ' != requested ' + qty, dbg);
                        }
                    }
                    return res;
                });
            });
            return Promise.all(sends).then(function (results) {
                var last = results[results.length - 1];
                if (last && last.ok) {
                    var lab = document.getElementById('md-step-order-label');
                    if (lab && last.order_label) {
                        var s = lab.querySelector('strong') || lab;
                        s.textContent = last.order_label;
                    }
                }
                setSaveStatus('saved');
                return results;
            }).catch(function (err) {
                console.error('[md] menu:save failed', err);
                setSaveStatus('error');
                throw err;
            }).finally(function () {
                flushInFlight = false;
                doSummary();
            });
        }

        function scheduleSave(mealId, plateId, qty) {
            pendingByLine[lineKey(mealId, plateId)] = qty;
            if (pendingTimer) {
                clearTimeout(pendingTimer);
            }
            setSaveStatus('saving');
            doSummary();
            pendingTimer = setTimeout(function () {
                pendingTimer = null;
                flushPending(false).catch(function () { /* logged */ });
            }, 400);
        }

        // Initial recompute (so summary reflects SSR truth even after browser back/forward).
        doSummary();
        log('menu:boot', {
            menuId: menuId,
            min: shell.getAttribute('data-min-meals'),
            exempt: shell.getAttribute('data-exempt-minimum')
        });

        wireMealDetailProduct(shell, doSummary);

        // Quantity input changes
        shell.addEventListener('input', function (e) {
            var t = e.target;
            if (!t || !t.classList || !t.classList.contains('md-meal-qty')) {
                return;
            }
            var v = parseInt(t.value, 10);
            if (isNaN(v) || v < 0) { v = 0; }
            if (v > 30) { v = 30; }
            if (String(v) !== t.value) {
                t.value = String(v);
            }
            var mid = parseInt(t.getAttribute('data-meal-id'), 10);
            var pid = parseInt(t.getAttribute('data-plate-id'), 10);
            if (isNaN(pid)) {
                pid = 0;
            }
            doSummary();
            scheduleSave(mid, pid, v);
        }, true);

        // +/- stepper buttons
        shell.addEventListener('click', function (e) {
            var t = e.target;
            if (!t || !t.closest) { return; }
            var minus = t.closest('.md-qty-minus');
            var plus = t.closest('.md-qty-plus');
            if (!minus && !plus) { return; }
            var stepper = minus || plus;
            var wrap = stepper.closest('.md-qty-stepper');
            if (!wrap) { return; }
            var inp = wrap.querySelector('.md-meal-qty');
            if (!inp || inp.disabled) {
                inp = wrap.querySelector('.md-meal-detail-add-qty');
            }
            if (!inp || inp.disabled) { return; }
            var isAdd = inp.classList.contains('md-meal-detail-add-qty');
            var lo = isAdd ? 1 : 0;
            var v = parseInt(inp.value, 10) || 0;
            if (minus) {
                v = Math.max(lo, v - 1);
            } else {
                v = Math.min(30, v + 1);
            }
            inp.value = String(v);
            inp.dispatchEvent(new Event('input', { bubbles: true }));
        });

        var contAnchors = shell.querySelectorAll('a.md-step-continue');
        var cq;
        for (cq = 0; cq < contAnchors.length; cq++) {
            (function (cont) {
                cont.addEventListener('click', function (ev) {
                    if (ev.defaultPrevented || ev.button !== 0) {
                        return;
                    }
                    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) {
                        return;
                    }
                    if (cont.classList.contains('is-disabled')) {
                        ev.preventDefault();
                        return;
                    }
                    var href = cont.getAttribute('href');
                    if (!href || href === '#') {
                        return;
                    }
                    if (Object.keys(pendingByLine).length === 0 && !pendingTimer && !flushInFlight) {
                        return;
                    }
                    ev.preventDefault();
                    log('continue: flushing then navigating', href);
                    flushPending(false).then(function () {
                        window.location.href = href;
                    }).catch(function () {
                        window.location.href = href;
                    });
                });
            })(contAnchors[cq]);
        }

        // Catch-all: flush on unload using keepalive so latest qty isn't lost
        function onUnload() {
            if (Object.keys(pendingByLine).length === 0 && !pendingTimer) {
                return;
            }
            flushPending(true);
        }
        window.addEventListener('beforeunload', onUnload);
        window.addEventListener('pagehide', onUnload);
    }

    /* ============================================================
     * CHECKOUT STEP
     * The form posts to itself (server handles save + redirect).
     * JS here only:
     *   - toggles tip chip UI + reveals custom field
     *   - shows a live tip/total preview
     *   - mirrors fulfillment hint visibility
     *   - keeps the notes char-counter in sync
     * ========================================================== */

    function readActiveTip(form) {
        var fixed = form.querySelector('.md-tip-row[data-mode="fixed"] .md-tip-chip.is-active');
        var pct = form.querySelector('.md-tip-row[data-mode="percent"] .md-tip-chip.is-active');
        if (fixed) {
            var fv = fixed.getAttribute('data-val');
            if (fv === 'custom') {
                var inp = form.querySelector('.md-tip-row[data-mode="fixed"] .md-tip-custom');
                var v = inp && inp.value !== '' ? Number(inp.value) : NaN;
                if (isNaN(v)) { return { mode: null, value: null }; }
                return { mode: 'fixed', value: v };
            }
            return { mode: 'fixed', value: Number(fv) };
        }
        if (pct) {
            var pv = pct.getAttribute('data-val');
            if (pv === 'custom') {
                var inp2 = form.querySelector('.md-tip-row[data-mode="percent"] .md-tip-custom');
                var v2 = inp2 && inp2.value !== '' ? Number(inp2.value) : NaN;
                if (isNaN(v2)) { return { mode: null, value: null }; }
                return { mode: 'percent', value: v2 };
            }
            return { mode: 'percent', value: Number(pv) };
        }
        return { mode: null, value: null };
    }

    function readSubtotalFromDOM() {
        var el = document.getElementById('md-order-subtotal');
        if (!el) { return 0; }
        var n = Number(String(el.textContent || '').replace(/[^0-9.]/g, ''));
        return isNaN(n) ? 0 : n;
    }

    function applyTipPreview(form) {
        var t = readActiveTip(form);
        var sub = readSubtotalFromDOM();
        var tip = 0;
        if (t.mode === 'fixed' && t.value != null && !isNaN(t.value)) {
            tip = t.value;
        } else if (t.mode === 'percent' && t.value != null && !isNaN(t.value)) {
            tip = sub * (t.value / 100);
        }
        tip = Math.round(tip * 100) / 100;
        var tipEl = document.getElementById('md-order-tip-display');
        var totEl = document.getElementById('md-order-total');
        if (tipEl) { tipEl.textContent = fmtMoney(tip); }
        if (totEl) { totEl.textContent = fmtMoney(sub + tip); }

        // Also sync the hidden tip_mode / tip_value form fields (added below in mountCheckout).
        var modeEl = form.querySelector('.md-tip-mode-input');
        var valEl = form.querySelector('.md-tip-value-input');
        if (modeEl) { modeEl.value = t.mode == null ? '' : String(t.mode); }
        if (valEl)  { valEl.value  = (t.value == null || isNaN(t.value)) ? '' : String(t.value); }
    }

    function wireTipFieldset(form) {
        var tip = form.querySelector('.md-tip');
        if (!tip || tip.getAttribute('data-readonly') === '1') {
            return;
        }
        function clearAll() {
            tip.querySelectorAll('.md-tip-chip.is-active').forEach(function (b) {
                b.classList.remove('is-active');
            });
            tip.querySelectorAll('.md-tip-custom').forEach(function (i) {
                i.setAttribute('hidden', '');
                i.value = '';
            });
        }
        tip.addEventListener('click', function (ev) {
            var btn = ev.target && ev.target.closest ? ev.target.closest('.md-tip-chip') : null;
            if (!btn || btn.disabled) { return; }
            var row = btn.closest('.md-tip-row');
            if (!row) { return; }
            var mode = row.getAttribute('data-mode');
            if (mode !== 'fixed' && mode !== 'percent') { return; }
            var val = btn.getAttribute('data-val');
            if (btn.classList.contains('is-active')) {
                clearAll();
                applyTipPreview(form);
                return;
            }
            clearAll();
            btn.classList.add('is-active');
            if (val === 'custom') {
                var ci = row.querySelector('.md-tip-custom');
                if (ci) {
                    ci.removeAttribute('hidden');
                    setTimeout(function () { ci.focus(); }, 0);
                }
            }
            applyTipPreview(form);
        });
        tip.addEventListener('input', function (ev) {
            var t = ev.target;
            if (t && t.classList && t.classList.contains('md-tip-custom')) {
                applyTipPreview(form);
            }
        });
    }

    function mountCheckout() {
        var form = document.getElementById('md-checkout-form');
        if (!form) { return; }
        if (form.closest('.md-prep-shell') && form.closest('.md-prep-shell').getAttribute('data-readonly') === '1') {
            return;
        }

        // Inject hidden tip_mode / tip_value inputs so the form POST carries
        // them even though the user picks tip via chip buttons.
        if (!form.querySelector('.md-tip-mode-input')) {
            var hm = document.createElement('input');
            hm.type = 'hidden';
            hm.name = 'tip_mode';
            hm.className = 'md-tip-mode-input';
            form.appendChild(hm);
        }
        if (!form.querySelector('.md-tip-value-input')) {
            var hv = document.createElement('input');
            hv.type = 'hidden';
            hv.name = 'tip_value';
            hv.className = 'md-tip-value-input';
            form.appendChild(hv);
        }

        wireTipFieldset(form);
        applyTipPreview(form);
        log('checkout:boot');

        // Fulfillment radio toggles the building hint.
        form.addEventListener('change', function (e) {
            var t = e.target;
            if (t && t.name === 'md_ff') {
                var hint = document.getElementById('md-order-building-hint');
                if (hint) { hint.hidden = t.value !== 'delivery'; }
                log('checkout:fulfillment', t.value);
            }
        });

        // Notes char counter
        var notes = document.getElementById('md-order-notes');
        var notesCount = document.getElementById('md-order-notes-count');
        if (notes && notesCount) {
            notes.addEventListener('input', function () {
                notesCount.textContent = String((notes.value || '').length);
            });
        }

        // Submit: nothing fancy — form POSTs naturally to the menu/checkout URL,
        // which is handled by the server-side template_redirect handler.
        form.addEventListener('submit', function () {
            applyTipPreview(form); // ensure hidden tip inputs are current
            log('checkout:submit', { place: !!form.querySelector('button[type=submit][name=place][value="1"]:focus') });
        });
    }

    /* ============================================================ */

    function ready() {
        try {
            mountCutoffCountdown();
        } catch (e) {
            console.error('[md] mountCutoffCountdown', e);
        }
        prep = typeof mdMealPrep !== 'undefined' ? mdMealPrep : {};
        try {
            mountMenuStep();
        } catch (e) {
            console.error('[md] mountMenuStep', e);
        }
        try {
            mountCheckout();
        } catch (e) {
            console.error('[md] mountCheckout', e);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', ready);
    } else {
        ready();
    }
})();
